import { yupResolver } from '@hookform/resolvers/yup'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack
} from '@mui/material'
import React, { FC, useEffect } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'

import useAccessTokenAndShopId from '../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import usePrintActionNowForMethod from '../../hooks/usePrintActionNowForMethod'
import {
  useEmailInvoicePdfMutation,
  useGetCustomersQuery,
  useGetRepairOrdersQuery
} from '../../redux/api'
import { PrintActionInfoMethodEnum } from '../../sdk'
import { formatName } from '../../utils/formatCustomerName/formatCustomerName'
import { ControlledMuiTextField } from '../FormField'

interface Props {
  open: boolean
  handleClose: () => void
}

interface FormInputs {
  email: string
  name: string
  subject: string
  body: string
}

const schema = yup
  .object({
    email: yup.string().email().required(),
    name: yup.string().required(),
    subject: yup.string().required(),
    body: yup.string().required()
  })
  .required()

const EmailInvoiceDialog: FC<Props> = ({ open, handleClose }) => {
  const { id } = useParams()
  const { accessToken, shopId } = useAccessTokenAndShopId()
  const { data: repairOrderList } = useGetRepairOrdersQuery({
    authorization: accessToken,
    shop: shopId,
    repairOrder: [id!]
  })

  const [emailInvoicePdf] = useEmailInvoicePdfMutation()

  const repairOrder = repairOrderList && repairOrderList[0]

  const { data: customers } = useGetCustomersQuery(
    {
      authorization: accessToken,
      shop: shopId,
      customer: [repairOrder!.entry!.info!.customer!]
    },
    {
      skip: !repairOrder
    }
  )

  const customer = customers && customers[0]

  const { handleSubmit, control, reset, setValue } = useForm<FormInputs>({
    defaultValues: {
      email: '',
      name: '',
      subject: '',
      body: ''
    },
    resolver: yupResolver(schema)
  })

  const createPrintActionNowForMethod = usePrintActionNowForMethod(
    repairOrder!.entry!.id!,
    accessToken
  )

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    emailInvoicePdf({
      authorization: accessToken,
      emailInvoiceRequest: {
        shop: shopId,
        repairOrderId: repairOrder!.entry!.id!,
        title: data.subject,
        body: data.body,
        toEmail: data.email,
        toName: data.name
      }
    })
    createPrintActionNowForMethod(PrintActionInfoMethodEnum.EmailInvoice)
    handleClose()
  }

  useEffect(() => {
    if (!open) {
      reset()
      return
    }
    if (customer && open) {
      const customerName = formatName(customer.information)

      setValue('email', customer.information!.email!)
      setValue('name', customerName)
    }
  }, [customer, setValue, open, reset])

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>Email Invoice</DialogTitle>
      <DialogContent>
        <form id="emailInvoice" onSubmit={handleSubmit(onSubmit)}>
          <Stack direction="column" spacing={2} paddingTop={2}>
            <ControlledMuiTextField
              required
              name="email"
              label="Customer's Email Address"
              control={control}
              InputProps={{ type: 'email' }}
            />
            <ControlledMuiTextField
              required
              name="name"
              label="Customer's Name"
              control={control}
            />
            <ControlledMuiTextField
              required
              name="subject"
              label="Email Subject"
              control={control}
            />
            <ControlledMuiTextField
              required
              multiline
              rows={4}
              name="body"
              label="Email Body"
              control={control}
            />
          </Stack>
        </form>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="text"
          form="emailInvoice"
          disabled={!repairOrder}>
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default EmailInvoiceDialog
